<template>
  <dialog-scroll-lock v-bind="$attrs" v-on="$listeners" width="400" :value="true">
    <v-card>
      <v-btn class="float-right mt-3 mr-3" icon @click="$emit('input', false)">
        <v-icon v-html="mdiClose" />
      </v-btn>
      <v-card-title class="pt-4 px-4 pb-0">{{canUpdate ? 'Add to g' : 'G'}}ift card</v-card-title>
      <v-card-subtitle v-if="(order.n_giftcard_recipients || 1) > 1" class="mt-0 px-4 py-0">
        This gift card will be sent to {{order.n_giftcard_recipients | pl('recipient')}}
      </v-card-subtitle>
      <v-card-subtitle v-if="userAmtAdded" class="mt-0 px-4 py-0">
        Amount you already contributed: <currency :code="blob.giftcard.cur" />{{userAmtAdded}}
      </v-card-subtitle>
      <v-card-subtitle class="mt-6 px-4">
        <v-card class="float-right ml-4 mb-1 gc-border" width="100">
          <v-img :aspect-ratio="337 / 213" :src="blob.giftcard.product_img" />
        </v-card>
        <div class="overline short" style="color: rgba(0, 0, 0, 0.87);" v-html="blob.giftcard.product_name" />
        <giftcard-details :src="blob.giftcard.src" :product-id="blob.giftcard.product_id" />
      </v-card-subtitle>
      <div style="clear: both;" />
      <v-divider />
      <v-row class="ma-1 mb-3 d-flex">
        <v-col cols="5">
          <div class="text--label-active mb-1">Current value</div>
          <v-btn style="pointer-events: none;" rounded dark color="green lighten-2">
            <currency :code="blob.giftcard.cur" />{{blob.giftcard.amt}}
          </v-btn>
        </v-col>
        <v-col class="d-flex flex-column justify-end align-center" cols="2">
          <v-btn v-if="amtValid" style="pointer-events: none;" rounded dark disabled>
            <v-icon style="color: rgba(0, 0, 0, 0.6) !important;" v-html="mdiArrowRightThin" />
          </v-btn>
        </v-col>
        <v-col class="text-right" cols="5">
          <div class="text--label-active mb-1">New value</div>
          <v-btn style="pointer-events: none;" rounded color="green" :dark="!!amtValid" :disabled="!amtValid">
            <currency :code="blob.giftcard.cur" />{{blob.giftcard.amt + (amtValid ? amt : 0)}}
          </v-btn>
        </v-col>
      </v-row>
      <v-card-text class="pa-0">
        <div v-if="locked" class="px-4 pb-4">
          <v-icon small v-html="mdiAlert" /> This order has been locked – this gift card can't be updated.
        </div>
        <div v-else-if="blob.giftcard.claim_id" class="px-4 pb-4">
          <v-icon small v-html="mdiAlert" /> This gift card has already been claimed – it can't be updated.
        </div>
        <div v-else-if="!allowedValues" class="px-4 pb-4">
          <v-icon small v-html="mdiAlert" /> This gift card is already at it's maximum value – it can't be updated.
        </div>
        <div v-else-if="blob.sample" class="px-4 pb-4">
          <v-icon small v-html="mdiAlert" /> This is a demo group gift card – it can't be updated.
        </div>
        <div v-else-if="userAmtAdded && !didAckUserAmtAdded" class="px-4 pb-4">
          <v-icon small v-html="mdiAlert" /> You already contributed <currency :code="blob.giftcard.cur" />{{userAmtAdded}} to this gift card. Are you sure you want to contribute more?
          <div class="mt-6 d-flex justify-end">
            <v-btn class="mr-3" @click="$emit('input', false)">Cancel</v-btn>
            <v-btn color="primary" @click="didAckUserAmtAdded = true">Yes</v-btn>
          </div>
        </div>
        <template v-else>
          <div class="px-4 text--label-active mb-n4">Add amount</div>
          <div class="px-4 pb-4">
            <v-form v-bind:class="{ 'py-8': allowedValues.fixed }" v-model="amtValid" @submit.prevent>
              <giftcard-amount :allowed-values="allowedValues" :cur="blob.giftcard.cur" :max="maxValue" v-model="amt" />
            </v-form>
          </div>
          <template v-if="!blob.sample">
            <v-divider />
            <payment
              v-if="amtValid"
              class="py-4 mx-auto"
              style="min-height: 414px;"
              passive-sign-in
              :metadata="paymentMetadata"
              @complete="onComplete"
            />
            <div v-else style="min-height: 414px;" />
          </template>
        </template>
      </v-card-text>
    </v-card>
  </dialog-scroll-lock>
</template>

<script>
import Currency from '@/components/Currency.vue';
import GiftcardAmount from '@/components/GiftcardAmount.vue';
import GiftcardDetails from '@/components/GiftcardDetails.vue';
import Payment from '@/components/Payment.vue';
import Price from '@/components/Price.vue';
import DialogScrollLockLoader from '@/components/DialogScrollLockLoader.vue';
import { getFee } from '@/utils/giftcard.js';
import { mdiAlert, mdiClose, mdiInformation, mdiFileDocument, mdiArrowRightThin } from '@mdi/js';

export default {
  name: 'giftcard-add-dialog',

  components: {
    Currency,
    GiftcardAmount,
    GiftcardDetails,
    Payment,
    Price,
    DialogScrollLockLoader
  },

  props: {
    orderId: String,
    order: Object,
    blob: Object,
    locked: Boolean,
    noMutation: Boolean,
    maxValue: { type: Number, default: 500000 }
  },

  data() {
    return {
      defaultAmt: null,
      amt: null,
      amtValid: false,
      didAckUserAmtAdded: false,

    };
  },

  created() {
    Object.assign(this, { mdiAlert, mdiClose, mdiInformation, mdiFileDocument, mdiArrowRightThin });
  },

  computed: {
    allowedValues() {
      if (!this.blob)
        return null;
      const amt = this.blob.giftcard.amt;
      const values = this.blob.giftcard.allowed_values;
      if (values.range) {
        const max = values.range.max - amt;
        if (max <= 0)
          return null;
        if (max == 1)
          return { fixed: [ 1 ] }
        return { range: { min: 1, max } };
      }
      const fixed = values.fixed.map(x => x - amt).filter(x => x > 0);
      if (!fixed.length)
        return null;
      return { fixed };
    },

    canUpdate() {
      return !this.locked && !this.blob.giftcard.claim_id && !!this.allowedValues;
    },

    paymentMetadata() {
      // Note that we're sure to keep this a valid non-zero object to make sure the payment stays loaded (and doesn't
      // change the height of the dialog)
      const amt = this.amtValid && this.amt || this.defaultAmt;
      return { target: 'GIFTCARD-CONTRIBUTION', order_id: this.orderId, blob_id: this.blob.id, amt };
    },

    userAmtAdded() {
      const all = _.cloneDeep(this.blob.giftcard.user_amt_added || {});
      const sum = Object.values(all).reduce((a, x) => a + x, 0);
      all[this.blob.user_id] ||= 0;
      all[this.blob.user_id] += this.blob.giftcard.amt - sum;
      return all[ this.global.profile?.user_id ] || 0;
    }
  },

  methods: {
    onComplete() {
      if (!this.noMutation) {
        // Don't bother making a service call here - just update the blob accordingly. Note that this does NOT udpate
        // the order's 'giftcard_unclaimed_v' property, but the front-end is currently agnostic to that.
        const userAmtAdded = this.blob.giftcard.user_amt_added || {};
        if (!userAmtAdded[this.globalUserId])
          userAmtAdded[this.globalUserId] = 0;
        userAmtAdded[this.globalUserId] += parseInt(this.amt);
        this.$set(this.blob.giftcard, 'user_amt_added', userAmtAdded);
        this.blob.giftcard.amt += parseInt(this.amt);
      }
      this.$emit('complete');
      this.$emit('input', false);
    }
  },

  watch: {
    allowedValues: {
      immediate: true,
      handler(v) {
        if (v)
          this.defaultAmt = this.amt = v.fixed ? v.fixed[0] : Math.min(5, v.range.max);
      }
    }
  }
}
</script>
